import React from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";

const SystemsFilters = ({
  filters,
  systemAdmins,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ p: 2, pb: 1.5, backgroundColor: theme.customColors.background.main }}>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search systems..."
          value={filters.searchQuery}
          onChange={(e) => filters.setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: filters.searchQuery && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => filters.setSearchQuery('')}
                  edge="end"
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              backgroundColor: 'white',
            },
          }}
        />
        
        <Button
          variant={filters.activeFiltersCount > 0 ? "contained" : "outlined"}
          startIcon={<FilterListIcon />}
          endIcon={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                transform: filters.showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            </Box>
          }
          onClick={() => filters.setShowFilters(!filters.showFilters)}
          sx={{
            minWidth: 140,
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            ...(filters.activeFiltersCount > 0 && {
              bgcolor: "#3B82F6",
              color: "#fff",
              '&:hover': {
                bgcolor: "#2563EB",
              },
            }),
            ...(filters.activeFiltersCount === 0 && {
              borderColor: "#3B82F6",
              color: "#3B82F6",
              '&:hover': {
                borderColor: "#2563EB",
                bgcolor: 'rgba(59, 130, 246, 0.08)',
              },
            }),
          }}
        >
          Filters
          {filters.activeFiltersCount > 0 && (
            <Chip
              label={filters.activeFiltersCount}
              size="small"
              sx={{
                ml: 1,
                height: 20,
                minWidth: 20,
                bgcolor: 'white',
                color: "#3B82F6",
                fontSize: '0.75rem',
                fontWeight: 600,
                '& .MuiChip-label': {
                  px: 0.75,
                },
              }}
            />
          )}
        </Button>
      </Box>

      <Collapse in={filters.showFilters}>
        <Box
          sx={{
            p: 2.5,
            backgroundColor: '#F9FAFB',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            <FormControl fullWidth size="small">
              <InputLabel>Account Owner</InputLabel>
              <Select
                value={filters.selectedSystemAdmin || ''}
                onChange={(e) => filters.setSelectedSystemAdmin(e.target.value || null)}
                label="Account Owner"
                sx={{
                  borderRadius: '8px',
                  backgroundColor: 'white',
                }}
              >
                <MenuItem value="">All Admins</MenuItem>
                {systemAdmins.map((admin) => (
                  <MenuItem key={admin} value={admin}>
                    {admin}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Critical Account</InputLabel>
              <Select
                value={filters.selectedIsCritical === null ? '' : filters.selectedIsCritical ? 'critical' : 'not_critical'}
                onChange={(e) => {
                  const value = e.target.value;
                  filters.setSelectedIsCritical(value === '' ? null : value === 'critical');
                }}
                label="Critical Account"
                sx={{
                  borderRadius: '8px',
                  backgroundColor: 'white',
                }}
              >
                <MenuItem value="">All Systems</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="not_critical">Not Critical</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={filters.selectedIntegrationType || ''}
                onChange={(e) => filters.setSelectedIntegrationType(e.target.value || null)}
                label="Source"
                sx={{
                  borderRadius: '8px',
                  backgroundColor: 'white',
                }}
              >
                <MenuItem value="">All Sources</MenuItem>
                <MenuItem value="MANUAL">Manual</MenuItem>
                <MenuItem value="AUTOMATED">Automated</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {filters.hasActiveFilters && (
            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                size="small"
                onClick={filters.clearFilters}
                startIcon={<ClearIcon />}
                sx={{
                  textTransform: 'none',
                  color: "#3B82F6",
                  fontWeight: 500,
                  '&:hover': {
                    bgcolor: 'rgba(59, 130, 246, 0.08)',
                  },
                }}
              >
                Clear all filters
              </Button>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {filters.selectedSystemAdmin && (
                  <Chip
                    label={`Admin: ${filters.selectedSystemAdmin}`}
                    onDelete={() => filters.removeFilter('system_admin')}
                    size="small"
                    sx={{
                      backgroundColor: "#3B82F6",
                      color: "#fff",
                      border: "2px solid #3B82F6",
                      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      height: "32px",
                      borderRadius: "12px",
                      '& .MuiChip-deleteIcon': {
                        color: "#fff",
                        '&:hover': {
                          color: "#E5E7EB",
                        },
                      },
                    }}
                  />
                )}
                {filters.selectedIsCritical !== null && (
                  <Chip
                    label={`Critical: ${filters.selectedIsCritical ? 'Critical' : 'Not Critical'}`}
                    onDelete={() => filters.removeFilter('is_critical')}
                    size="small"
                    sx={{
                      backgroundColor: "#3B82F6",
                      color: "#fff",
                      border: "2px solid #3B82F6",
                      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      height: "32px",
                      borderRadius: "12px",
                      '& .MuiChip-deleteIcon': {
                        color: "#fff",
                        '&:hover': {
                          color: "#E5E7EB",
                        },
                      },
                    }}
                  />
                )}
                {filters.selectedIntegrationType && (
                  <Chip
                    label={`Source: ${filters.selectedIntegrationType === 'MANUAL' ? 'Manual' : 'Automated'}`}
                    onDelete={() => filters.removeFilter('type')}
                    size="small"
                    sx={{
                      backgroundColor: "#3B82F6",
                      color: "#fff",
                      border: "2px solid #3B82F6",
                      boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      height: "32px",
                      borderRadius: "12px",
                      '& .MuiChip-deleteIcon': {
                        color: "#fff",
                        '&:hover': {
                          color: "#E5E7EB",
                        },
                      },
                    }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default SystemsFilters;

